import { memo, useCallback, useMemo, useState } from 'react';
import type { ScrollToBottom } from 'use-stick-to-bottom';
import { Markdown } from '~/components/chat/Markdown';
import { PinnedMessage } from '~/components/chat/PinnedMessage';
import { Collapsible } from '~/components/ui/Collapsible';
import { LegacyButton } from '~/components/ui/LegacyButton';
import { Dialog, DialogActions, DialogButton, DialogDescription, DialogTitle } from '~/components/ui/LegacyDialog';
import { supabaseStore, type MigrationData, type MigrationsData } from '~/lib/stores/supabase';
import type { SupabaseDBChangeMessage } from '~/types/chat';
import { classNames } from '~/utils/classNames';
import { invariant } from '~/utils/invariant';
import { plural } from '~/utils/plural';

import styles from './SupabaseMigration.module.scss';

export interface SupabaseMigrationProps {
  change: MigrationsData;
  isLast: boolean;
  messageBoxHeight: number;
  changeStatus: SupabaseDBChangeMessage['changeStatus'];
  scrollToBottom: ScrollToBottom;
  onStickyHeight: (height: number) => void;
  acceptSupabaseChange: (changeId: string) => Promise<void>;
}

interface ToggledState {
  [migrationFilePath: string]: boolean | undefined;
}

export function SupabaseMigration({
  change,
  messageBoxHeight,
  isLast,
  changeStatus,
  scrollToBottom,
  onStickyHeight,
  acceptSupabaseChange,
}: SupabaseMigrationProps) {
  const [migrationSectionToggledState, setMigrationSectionToggledState] = useState<ToggledState>({});

  const toggled = useMemo(() => {
    return Object.values(migrationSectionToggledState).some((isToggled) => isToggled);
  }, [migrationSectionToggledState]);

  const pending = changeStatus === 'pending';
  const applied = changeStatus === 'applied';
  const failed = changeStatus === 'failed';
  const discarded = changeStatus === 'discarded';

  const [applying, setApplying] = useState(false);
  const [showDialog, setShowDialog] = useState(false);

  const failedOrDiscarded = failed || discarded;

  const discardChanges = useCallback(() => {
    setShowDialog(false);

    return supabaseStore.discardChanges(change.id);
  }, [change.id]);

  const getTitle = () => {
    const migrationsCount = change.migrations.length;
    const baseMessage = `The database ${plural(migrationsCount, 'migration has', 'migrations have')}`;

    const statusMessages = {
      applied: `${baseMessage} been applied`,
      failed: `${baseMessage} not been applied correctly`,
      discarded: `${baseMessage} been discarded`,
      pending: 'Supabase Migrations',
    };

    return statusMessages[changeStatus];
  };

  return (
    <>
      <PinnedMessage
        toggled={toggled}
        setToggled={unusedCallback}
        sticky={!applied && !failedOrDiscarded}
        title={
          <div
            className={classNames('flex items-center gap-2', {
              'text-emerald-600 dark:text-emerald-400': !failedOrDiscarded,
              'text-bolt-elements-icon-error': failedOrDiscarded,
            })}
          >
            <div className="grid grid-areas-[stack] items-center justify-items-center">
              <div
                className={classNames('i-logos:supabase-icon text-lg [grid-area:stack] opacity-0', {
                  'opacity-100': !applying && !applied && !failedOrDiscarded,
                })}
              />
              <div
                className={classNames(
                  'i-ph:check size-4.5 text-emerald-600 dark:text-emerald-400 [grid-area:stack] opacity-0',
                  {
                    'opacity-100': !applying && applied,
                  },
                )}
              />
              <div
                className={classNames('i-ph:x size-4.5 text-bolt-elements-icon-error [grid-area:stack] opacity-0', {
                  'opacity-100': !applying && failedOrDiscarded,
                })}
              />
              <div
                className={classNames(
                  'i-svg-spinners:90-ring-with-bg size-4.5 text-emerald-600 dark:text-emerald-400 [grid-area:stack] opacity-0',
                  {
                    'opacity-100': applying,
                  },
                )}
              />
            </div>
            <span className={classNames({ 'text-bolt-elements-textPrimary': !pending })}>{getTitle()}</span>
          </div>
        }
        actions={
          <>
            {change.migrations.map((migration) => (
              <MigrationSection
                key={migration.filePath}
                migration={migration}
                scrollToBottom={scrollToBottom}
                setToggled={(toggled) =>
                  setMigrationSectionToggledState((toggledState) => ({
                    ...toggledState,
                    [migration.filePath]: toggled,
                  }))
                }
                toggled={!!migrationSectionToggledState[migration.filePath]}
              />
            ))}
            {!applied && !failedOrDiscarded && (
              <div className="flex flex-col gap-4 justify-between">
                <p>Please review the proposed changes and apply them to your database.</p>
                <div className="flex gap-2.5">
                  <LegacyButton
                    variant="secondary"
                    className="gap-2 bg-bolt-elements-button-supabase-background [&:not(:disabled,.disabled)]:hover:bg-bolt-elements-button-supabase-backgroundHover text-white transition-theme"
                    size="sm"
                    disabled={applying}
                    onClick={() => {
                      scrollToBottom?.();

                      setMigrationSectionToggledState((currentToggleState) => {
                        // mark all entries as not toggled (false)
                        return Object.fromEntries(Object.keys(currentToggleState).map((filePath) => [filePath, false]));
                      });

                      setApplying(true);

                      acceptSupabaseChange(change.id).finally(() => {
                        setApplying(false);
                      });
                    }}
                  >
                    {applying ? 'Applying changes…' : 'Apply changes'}
                  </LegacyButton>

                  <LegacyButton variant="warn" size="sm" disabled={applying} onClick={() => setShowDialog(true)}>
                    Discard
                  </LegacyButton>
                </div>
              </div>
            )}
          </>
        }
        messageBoxHeight={messageBoxHeight}
        messageClassName={classNames('before:border-brands-supabase-accent', {
          'before:border-l-3': isLast && pending,
        })}
        isLast={isLast}
        scrollToBottom={scrollToBottom}
        onStickyHeight={onStickyHeight}
      />

      <Dialog open={showDialog} showCloseButton={false} onClose={() => setShowDialog(false)} maxWidth={600}>
        <DialogTitle>Supabase Migrations</DialogTitle>
        <DialogDescription>
          <p>You are about to discard the latest database changes.</p>
          <p className="my-6 bg-alpha-orange-10 border-l border-l-2 border-orange-500 p-2 px-4 text-orange-600 dark:text-orange-500 [&_a]:underline [&_a]:text-bolt-elements-link hover:[&_a]:text-bolt-elements-linkHover">
            If you discard the changes, your application might end up in a broken state because it might have been
            modified to work with the latest database changes. It's almost always better to apply the changes and then
            refine them later.
          </p>
          <p>Are you sure you want to discard?</p>
        </DialogDescription>

        <DialogActions>
          <DialogButton variant="secondary" onClick={() => setShowDialog(false)}>
            Cancel
          </DialogButton>
          <DialogButton variant="warn" onClick={() => discardChanges()}>
            Yes, Discard
          </DialogButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

interface MigrationSectionProps {
  migration: MigrationData;
  toggled: boolean;
  setToggled: (toggled: boolean) => void;
  scrollToBottom: ScrollToBottom;
}

const MigrationSection = memo<MigrationSectionProps>(({ migration, scrollToBottom, setToggled, toggled }) => {
  return (
    <Collapsible
      title={
        <div className="flex items-center gap-2.5 p-3 grow text-bolt-elements-textPrimary leading-5 text-sm">
          <span className="i-ph:database size-4" />
          <span className="font-medium">{migration.shortSummary || 'Database Changes'}</span>
        </div>
      }
      titleClassName="bg-bolt-elements-artifacts-background"
      toggleButtonClassName="bg-bolt-elements-artifacts-background"
      open={toggled}
      scrollToBottom={scrollToBottom}
      onClick={() => setToggled(!toggled)}
      onToggle={setToggled}
    >
      <div className="flex flex-col gap-4 p-5 text-left bg-bolt-elements-artifacts-background">
        <Markdown className={styles.MigrationSummary} scrollToBottom={scrollToBottom}>
          {migration.longSummary}
        </Markdown>
      </div>
    </Collapsible>
  );
});

function unusedCallback() {
  // this function is not expected to be called
  invariant(false, 'Callback should not be used!');
}
